import { useState, useEffect } from 'react';
import axios from 'axios';
import { Palette, Save, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_COLORS = [
  { age: 0, name: 'verde', hex: '#22c55e' },
  { age: 1, name: 'azul', hex: '#2563eb' },
  { age: 2, name: 'rojo', hex: '#ef4444' },
  { age: 3, name: 'naranja', hex: '#f97316' },
  { age: 4, name: 'amarillo', hex: '#eab308' },
];

export default function YearColorConfig({ open, onClose }) {
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const token = localStorage.getItem('veloflux_token');
    axios.get(`${API}/config/year-colors`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setColors(res.data.colors))
      .catch(() => setColors(DEFAULT_COLORS))
      .finally(() => setLoading(false));
  }, [open]);

  const updateColor = (age, field, value) => {
    setColors(prev => prev.map(c => c.age === age ? { ...c, [field]: value } : c));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('veloflux_token');
      await axios.put(`${API}/config/year-colors`, { colors }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Colores actualizados correctamente');
      onClose();
    } catch {
      toast.error('Error al guardar los colores');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setColors(DEFAULT_COLORS);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="year-color-config-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-orange-500" />
            Configurar Colores por Año
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="spinner w-6 h-6" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">
              Asigna un color a cada nivel de antigüedad. Año 0 = bicicletas del año actual.
            </p>
            <div className="space-y-3">
              {colors.sort((a, b) => a.age - b.age).map(item => (
                <div key={item.age} className="flex items-center gap-3" data-testid={`color-row-${item.age}`}>
                  <div className="w-16 text-sm font-medium text-slate-700">
                    Año {item.age}
                  </div>
                  <div
                    className="w-8 h-8 rounded-full border-2 border-slate-200 flex-shrink-0 cursor-pointer relative overflow-hidden"
                    style={{ backgroundColor: item.hex }}
                  >
                    <input
                      type="color"
                      value={item.hex}
                      onChange={(e) => updateColor(item.age, 'hex', e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                      data-testid={`color-picker-${item.age}`}
                    />
                  </div>
                  <Input
                    value={item.name}
                    onChange={(e) => updateColor(item.age, 'name', e.target.value)}
                    placeholder="Nombre del color"
                    className="flex-1"
                    data-testid={`color-name-${item.age}`}
                  />
                  <code className="text-xs text-slate-400 w-16">{item.hex}</code>
                </div>
              ))}
            </div>

            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
              <strong>Rotación automática:</strong> Cuando cambie el año, las bicicletas envejecen automáticamente. 
              Las del año actual siempre tendrán el color de "Año 0".
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" size="sm" onClick={handleReset} data-testid="reset-colors-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Restablecer
          </Button>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            className="bg-orange-500 hover:bg-orange-600"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-colors-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
